"use client";

import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  isNavigationLink,
  isNavigationSectionLabel,
  isSidebarNavigationItemActive,
  type SidebarNavigationLinkOrGroup,
  type SidebarNavigationStructure,
} from "./navigation";
import SideNavigationItem from "./SideNavigationItem";
import SideNavigationSection from "./SideNavigationSection";

export type NavigationRoute = {
  pathPrefix: string;
  structure: SidebarNavigationStructure;
};

export default function SideNavigation({
  routes,
  defaultStructure,
  bannerActive = false,
  systemStatusWidget,
}: {
  routes?: NavigationRoute[];
  defaultStructure: SidebarNavigationStructure;
  bannerActive?: boolean;
  systemStatusWidget?: React.ReactNode;
}) {
  const pathname = usePathname();

  const structure = useMemo(() => {
    if (routes) {
      for (const route of routes) {
        if (pathname.startsWith(route.pathPrefix)) {
          return route.structure;
        }
      }
    }
    return defaultStructure;
  }, [pathname, routes, defaultStructure]);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Close mobile nav when navigating to a new page
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useLayoutEffect(() => {
    scrollContainerRef.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [pathname]);

  const findActiveTitle = (): string => {
    for (const entry of structure) {
      if (isNavigationSectionLabel(entry)) continue;
      const item = entry as SidebarNavigationLinkOrGroup;
      if (isSidebarNavigationItemActive(item, pathname)) {
        return item.title;
      }
    }
    return "Documentation";
  };

  // API reference owns its endpoint and schema navigation.
  if (pathname === "/docs/api-reference" || pathname.startsWith("/docs/api-reference/")) return null;

  return (
    <aside className="relative">
      <div
        style={{
          height: isMobile ? (open ? "60vh" : "48px") : undefined,
          transition: isMobile
            ? "height 700ms cubic-bezier(0.16, 1, 0.3, 1)"
            : undefined,
        }}
        className={clsx(
          "lg:w-[calc(322px-32px)] shrink-0 z-15 flex flex-col lg:justify-between sticky lg:p-0 p-0.5 lg:max-h-[calc(100vh-180px)] lg:h-[calc(100vh-180px)]",
          isMobile ? "overflow-hidden" : "",
          bannerActive ? "lg:top-32 top-[120px]" : "lg:top-24 top-[76px]",
        )}
      >
        <div className="bg-grey-900 absolute w-[calc(100vw-16px)] top-0 bottom-0 left-1/2 -translate-x-1/2 pointer-events-none lg:hidden rounded-lg" />

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="relative z-10 text-sm font-medium mb-1 py-1 items-center flex gap-2 pl-2.5 mt-2 rounded lg:hidden"
        >
          <div
            className={clsx(
              "w-2.5 h-[13px] aspect-square flex flex-col justify-between relative transition-transform -ml-1.5 mr-0.5",
              open && "text-grey-800",
            )}
          >
            <div
              className={clsx(
                "h-[3px] w-[3px] rounded-full bg-white absolute left-1/2 -translate-x-1/2 transition-all duration-200",
                !open
                  ? "opacity-100 top-0"
                  : "opacity-0 top-1/2 -translate-y-1/2 right-0",
              )}
            />
            <div className="h-[3px] w-[3px] rounded-full bg-white absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
            <div
              className={clsx(
                "h-[3px] w-[3px] rounded-full bg-white absolute left-1/2 -translate-x-1/2 transition-all duration-200",
                !open
                  ? "opacity-100 bottom-0"
                  : "opacity-0 top-1/2 -translate-y-1/2 left-0",
              )}
            />
          </div>
          {findActiveTitle()}
        </button>

        {/* Navigation items */}
        <div
          ref={scrollContainerRef}
          style={
            isMobile
              ? {
                  opacity: open ? 1 : 0,
                  transition: open
                    ? "opacity 500ms cubic-bezier(0.16, 1, 0.3, 1) 200ms"
                    : "opacity 300ms ease",
                  pointerEvents: open ? "auto" : "none",
                }
              : undefined
          }
          className={clsx(
            "flex flex-col gap-2 -ml-1 relative z-10 px-2 lg:-mx-2 lg:overflow-y-auto pt-3 custom-scrollbar",
            !isMobile && "opacity-100",
            isMobile && "overflow-y-auto",
          )}
        >
          {structure.map((item, i) => {
            if (isNavigationSectionLabel(item)) {
              return (
                <div
                  key={`section-${item.title}`}
                  className={i > 0 ? "mt-3" : ""}
                >
                  <SideNavigationSection label={item} />
                </div>
              );
            } else {
              const navItem = item as SidebarNavigationLinkOrGroup;
              return (
                <SideNavigationItem
                  key={isNavigationLink(navItem) ? navItem.href : navItem.title}
                  item={navItem}
                  pathname={pathname}
                  depth={0}
                />
              );
            }
          })}
          <div className="h-24" />
        </div>

        {/* System status widget at bottom */}
        {systemStatusWidget && (
          <div className="hidden lg:block mt-4 px-2 lg:-mx-2 shrink-0">
            {systemStatusWidget}
          </div>
        )}
      </div>
    </aside>
  );
}
